/**
 * QM8D — AuthContext: Zentrales Auth-State-Management
 * ADR-001: IMPLEMENTED — HttpOnly Cookies + In-Memory Bearer Fallback (Form-Login)
 * ADR-048 (FIX-4 / FIND-C6): Entra-SSO ist jetzt Cookie-Only — kein URL-Fragment
 *
 * AUTH-STRATEGIE:
 * - **Form-Login**: Hybrid (Cookie primaer, Memory-Bearer-Fallback fuer
 *   Cross-Origin-Custom-Domain). Tokens kommen aus JSON-Body, nicht aus URL.
 * - **Entra-SSO**: Cookie-Only (FIX-4). Tokens fliessen NIE durch URL,
 *   Fragment oder Query — nur ueber HttpOnly-Secure-Cookies.
 *
 * Verantwortlichkeiten:
 * - Auth-State (user, loading)
 * - Login/Logout Funktionen (Form-Login behaelt Memory-Tokens fuer Bearer)
 * - Session-Wiederherstellung via Cookie (/auth/me)
 * - Theme-Initialisierung nach Login
 */
import React, { useState, useEffect, createContext } from 'react';
import axios from 'axios';
import { API, setMemoryTokens, clearMemoryTokens } from '../lib/axiosClient';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  // /report-render ist ein headless Render-Kontext (Playwright/PDF-Export):
  // KEIN Auth-Gate, KEIN /auth/me. Die Seite nutzt einen Render-Token + serverseitig
  // injizierte Daten (window.__REPORT_DATA__). So blockiert ein langsamer
  // /auth/me-Egress-Hairpin in Produktion NICHT das Rendern (Ready-Sentinel).
  const isReportRender = typeof window !== 'undefined'
    && window.location.pathname.startsWith('/report-render');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!isReportRender);

  useEffect(() => {
    if (isReportRender) {
      setLoading(false);
      return;
    }
    // FIX-4 / FIND-C6: KEIN URL-Fragment-Reader mehr — Cookies allein.
    // Cookies werden vom Browser verwaltet — direkt fetchUser
    // Wenn Cookie gueltig -> /auth/me liefert User
    // Wenn Cookie ungueltig -> 401 -> User bleibt null
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { _skipAuthRetry: true });
      setUser(res.data);
      try {
        const prefsRes = await axios.get(`${API}/user/preferences`);
        const prefs = prefsRes.data || {};
        if (prefs && Object.keys(prefs).length > 0) {
          const { applyThemeVars, cacheThemePrefs } = await import('../pages/theme/themeConstants');
          applyThemeVars(prefs);
          cacheThemePrefs(prefs);
        }
      } catch {}
      // QM8D Bug-Fix P2-4 (2026-05-27): authentifizierte Initial-Polls
      // (Sprache, Badges, ...) lauschen auf dieses Event statt eigene
      // 401-erzeugende Polls direkt beim Mount auszuloesen.
      try {
        window.dispatchEvent(new CustomEvent('qm8d:auth:ready', { detail: { user: res.data } }));
      } catch {}
    } catch (error) {
      // Cookie ungueltig oder nicht vorhanden — User bleibt null
      clearMemoryTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, accessToken, refreshToken) => {
    // Form-Login: Tokens werden zusaetzlich in Memory gehalten als
    // Bearer-Fallback fuer Cross-Origin-Custom-Domain (ADR-001).
    // FIX-4: Entra-Callback ruft login() nicht mehr selbst auf — der
    // Browser uebernimmt die Cookies direkt aus dem 302-Redirect.
    if (accessToken) {
      setMemoryTokens(accessToken, refreshToken);
    }
    setUser(userData);
    // Bug-Fix P2-4: nach Login Event triggern, damit Init-Pollers ohne
    // weiteren Reload sofort ihre Daten holen koennen.
    try {
      window.dispatchEvent(new CustomEvent('qm8d:auth:ready', { detail: { user: userData } }));
    } catch {}
  };

  const logout = () => {
    // Backend liest refresh_token aus Cookie und loescht Cookies in Response
    axios.post(`${API}/auth/logout`).catch(() => {});
    clearMemoryTokens();
    localStorage.removeItem('qm8d_theme');
    localStorage.removeItem('qm8d_theme_prefs');
    document.documentElement.classList.remove('dark');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto" />
          <p className="mt-3 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
